<template>
  <div class="password-strength">
    <!-- Strength Meter -->
    <div class="strength-meter">
      <div class="strength-bar">
        <div 
          class="strength-bar-fill" 
          :class="`strength-${strengthLevel}`"
          :style="{ width: strengthPercentage + '%' }"
        ></div>
      </div>
      <div v-if="password" class="strength-text">
        <span class="strength-label">Password Strength:</span>
        <span :class="`strength-value strength-${strengthLevel}`">{{ strengthText }}</span>
      </div>
      <div v-else class="strength-text-placeholder">
        <span class="strength-label">Password Strength:</span>
        <span class="strength-value">Enter a password to see strength</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PasswordStrength',
  props: {
    password: {
      type: String,
      default: ''
    },
    policy: {
      type: Object,
      default: null
    }
  },
  computed: {
    strengthLevel() {
      if (!this.password) return 0;
      
      let score = 0;
      
      // Length scoring
      if (this.password.length >= (this.policy && this.policy.minimumLength || 16)) score += 20;
      if (this.password.length >= 20) score += 10;
      
      // Character variety
      if (/[A-Z]/.test(this.password)) score += 15;
      if (/[a-z]/.test(this.password)) score += 15;
      if (/\d/.test(this.password)) score += 15;
      if (/[!@#$%^&*()_+=\[\]{};:<>|./?,-]/.test(this.password)) score += 25;
      
      // Complexity bonus
      const uniqueChars = new Set(this.password).size;
      score += Math.min(uniqueChars * 1, 10);
      
      // Pattern penalties
      if (/(.)\1{2,}/.test(this.password)) score -= 10; // Repeated characters
      if (/123|abc|qwe/i.test(this.password)) score -= 10; // Sequential patterns
      
      // Determine strength level (0-4)
      if (score >= 80) return 4; // Very Strong
      if (score >= 60) return 3; // Strong
      if (score >= 40) return 2; // Fair
      if (score >= 20) return 1; // Weak
      return 0; // Very Weak
    },
    
    strengthText() {
      const levels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
      return levels[this.strengthLevel];
    },
    
    strengthPercentage() {
      return Math.max((this.strengthLevel + 1) * 20, 20);
    }
  }
};
</script>

<style scoped>
.password-strength {
}

/* Strength Meter */
.strength-meter {
  margin-top: 10px;
}

.strength-bar {
  width: 100%;
  height: 8px;
  background-color: #F0F4F5;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.strength-bar-fill {
  height: 100%;
  transition: width 0.3s ease, background-color 0.3s ease;
  border-radius: 4px;
}

/* Strength level colors */
.strength-0 { background-color: #D5281B; }
.strength-1 { background-color: #F47738; }
.strength-2 { background-color: #F2C94C; }
.strength-3 { background-color: #7FBB44; }
.strength-4 { background-color: #006747; }

.strength-text, .strength-text-placeholder {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.strength-label {
  font-weight: 600;
  color: white;
  padding: 4px;
}

.strength-value {
  font-weight: 600;
  padding: 5px;
  border-radius: 5px;
  color: white;
}

.strength-text-placeholder .strength-value {
  font-weight: normal;
} 
</style>